import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import FormShell from "@/components/FormShell";
import PersonalInfoStep from "@/components/PersonalInfoStep";
import FileUploader from "@/components/FileUploader";
import DatePicker from "@/components/DatePicker";
import CustomFieldsRenderer from "@/components/CustomFieldsRenderer";
import { useSiteConfig } from "@/lib/siteConfig";
import { api } from "@/lib/api";

const STEPS = ["Personal information", "Life profile & beneficiary", "Coverage & documents"];

export default function LifeForm() {
  const navigate = useNavigate();
  const { config } = useSiteConfig();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [personal, setPersonal] = useState({});
  const [life, setLife] = useState({ smoker: false });
  const [files, setFiles] = useState([]);
  const [custom, setCustom] = useState({});
  const customFields = config.life_custom_fields || [];

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const validatePersonal = () => {
    const required = ["full_name", "email", "phone", "dob", "gender", "address", "city", "state", "postal_code", "id_number"];
    for (const f of required) {
      if (!personal[f]) {
        toast.error(`Please fill in ${f.replace("_", " ")}`);
        return false;
      }
    }
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        insurance_type: "life",
        ...personal,
        life_details: {
          ...life,
          annual_income: life.annual_income ? Number(life.annual_income) : null,
          coverage_amount: life.coverage_amount ? Number(life.coverage_amount) : null,
          policy_term_years: life.policy_term_years ? Number(life.policy_term_years) : null,
        },
        file_ids: files.map((f) => f.id),
        custom_fields: custom,
      };
      const { data } = await api.post("/clients", payload);
      toast.success("Application submitted successfully!");
      navigate("/apply/success", { state: { tracker_url: data.tracker_url, tracker_token: data.tracker_token } });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormShell
      title={
        <span className="inline-flex items-center gap-3">
          <Shield className="w-7 h-7 text-clay" strokeWidth={1.5} /> Life Insurance Application
        </span>
      }
      subtitle={STEPS[step]}
      step={step}
      totalSteps={STEPS.length}
    >
      {step === 0 && <PersonalInfoStep data={personal} set={setPersonal} />}

      {step === 1 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label className="text-forest">Occupation</Label>
            <Input
              value={life.occupation || ""}
              onChange={(e) => setLife({ ...life, occupation: e.target.value })}
              className="mt-1.5 h-11 border-line input-clean"
              data-testid="input-occupation"
            />
          </div>
          <div>
            <Label className="text-forest">Annual income (USD)</Label>
            <Input
              type="number"
              value={life.annual_income || ""}
              onChange={(e) => setLife({ ...life, annual_income: e.target.value })}
              className="mt-1.5 h-11 border-line input-clean"
              data-testid="input-income"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-forest">High-risk activities <span className="text-sage font-normal">(skydiving, racing, etc.)</span></Label>
            <Textarea
              value={life.high_risk_activities || ""}
              onChange={(e) => setLife({ ...life, high_risk_activities: e.target.value })}
              rows={2}
              className="mt-1.5 border-line input-clean resize-none"
              data-testid="input-risk-activities"
            />
          </div>
          <div className="flex items-center justify-between bg-cream border border-line rounded-sm px-4 py-3 md:col-span-2">
            <Label className="text-forest">Smoker</Label>
            <Switch
              checked={!!life.smoker}
              onCheckedChange={(v) => setLife({ ...life, smoker: v })}
              data-testid="switch-smoker-life"
            />
          </div>
          <div className="md:col-span-2 pt-4 border-t border-line">
            <h3 className="font-heading text-lg mb-1">Beneficiary</h3>
            <p className="text-sm text-sage mb-4">The person who receives the policy benefit.</p>
          </div>
          <div>
            <Label className="text-forest">Beneficiary full name</Label>
            <Input
              value={life.beneficiary_name || ""}
              onChange={(e) => setLife({ ...life, beneficiary_name: e.target.value })}
              className="mt-1.5 h-11 border-line input-clean"
              data-testid="input-beneficiary-name"
            />
          </div>
          <div>
            <Label className="text-forest">Relationship</Label>
            <Select value={life.beneficiary_relation || ""} onValueChange={(v) => setLife({ ...life, beneficiary_relation: v })}>
              <SelectTrigger className="mt-1.5 h-11 border-line" data-testid="select-beneficiary-relation">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="spouse">Spouse</SelectItem>
                <SelectItem value="child">Child</SelectItem>
                <SelectItem value="parent">Parent</SelectItem>
                <SelectItem value="sibling">Sibling</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-forest">Beneficiary date of birth</Label>
            <div className="mt-1.5">
              <DatePicker
                value={life.beneficiary_dob || ""}
                onChange={(v) => setLife({ ...life, beneficiary_dob: v })}
                placeholder="Pick a date"
                testId="input-beneficiary-dob"
              />
            </div>
          </div>
          <div>
            <Label className="text-forest">Existing policies <span className="text-sage font-normal">(optional)</span></Label>
            <Input
              value={life.existing_policies || ""}
              onChange={(e) => setLife({ ...life, existing_policies: e.target.value })}
              className="mt-1.5 h-11 border-line input-clean"
              data-testid="input-existing-policies"
            />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label className="text-forest">Coverage amount (USD)</Label>
              <Input
                type="number"
                value={life.coverage_amount || ""}
                onChange={(e) => setLife({ ...life, coverage_amount: e.target.value })}
                placeholder="e.g. 1000000"
                className="mt-1.5 h-11 border-line input-clean"
                data-testid="input-coverage-life"
              />
            </div>
            <div>
              <Label className="text-forest">Policy term (years)</Label>
              <Select value={String(life.policy_term_years || "")} onValueChange={(v) => setLife({ ...life, policy_term_years: v })}>
                <SelectTrigger className="mt-1.5 h-11 border-line" data-testid="select-policy-term">
                  <SelectValue placeholder="Select term" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 years</SelectItem>
                  <SelectItem value="20">20 years</SelectItem>
                  <SelectItem value="30">30 years</SelectItem>
                  <SelectItem value="0">Whole life</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <FileUploader
            files={files}
            setFiles={setFiles}
            label="Supporting documents (ID proof, income proof, existing policy docs)"
            testId="life-uploader"
          />
          {customFields.length > 0 && (
            <div className="pt-6 border-t border-line">
              <h3 className="font-heading text-lg mb-1">Additional information</h3>
              <p className="text-sm text-sage mb-4">Help your agent recommend the right product mix.</p>
              <CustomFieldsRenderer
                fields={customFields}
                values={custom}
                onChange={(id, v) => setCustom((c) => ({ ...c, [id]: v }))}
              />
            </div>
          )}
        </div>
      )}

      <div className="flex justify-between mt-10 pt-6 border-t border-line">
        <Button
          variant="outline"
          onClick={prev}
          disabled={step === 0}
          className="border-line text-sage hover:text-forest hover:border-forest rounded-sm h-11 px-5"
          data-testid="form-prev-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        {step < STEPS.length - 1 ? (
          <Button
            onClick={() => {
              if (step === 0 && !validatePersonal()) return;
              next();
            }}
            className="bg-forest hover:bg-forestdark text-bone rounded-sm h-11 px-6"
            data-testid="form-next-btn"
          >
            Continue <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-clay hover:bg-claydark text-bone rounded-sm h-11 px-6"
            data-testid="form-submit-btn"
          >
            {submitting ? "Submitting…" : "Submit application"} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
    </FormShell>
  );
}
